/**
 * Damage Rolls: the engine's formula for the degree of success (critical doubling, deadly and
 * fatal dice, critical-only parts) rolled die by die, with persistent damage kept apart from what
 * lands now and precision and splash shares typed on their own, as Foundry's DamageRoll reports them.
 */
import type { DieResult, RolledInstance } from "./dice";
import { diceOf, rollFormula } from "./dice";
import type { DiceSupply } from "./random";

export interface DamageInstanceResult {
  type: string;
  kind: "damage" | "healing" | "both";
  persistent: boolean;
  total: number;
  /** The share of the total that is precision damage and splash damage. */
  precision: number;
  splash: number;
  formula: string;
  materials: string[];
  dice: DieResult[];
}

export interface DamageResult {
  formula: string;
  critical: boolean;
  instances: DamageInstanceResult[];
  /** What lands now: every non-persistent damage instance, at least 1 when any (Foundry raises a non-positive total to 1). */
  total: number;
  healing: number;
  /** Persistent damage, rolled and shown apart; it is not part of `total`. */
  persistent: DamageInstanceResult[];
  splash: number;
  precision: number;
  /** The total before it was raised to 1, when it was. */
  increasedFrom: number | null;
  dice: DieResult[];
  physical: boolean;
}

function instanceResult(i: RolledInstance): DamageInstanceResult {
  const share = (category: "precision" | "splash"): number => Math.max(0, Math.floor(i.parts.filter((p) => p.category === category).reduce((s, p) => s + p.value, 0)));
  return { type: i.type, kind: i.kind, persistent: i.persistent, total: i.total, precision: share("precision"), splash: share("splash"), formula: i.formula, materials: i.materials, dice: i.dice };
}

/** Roll a damage formula. `critical` only marks the result: the caller passes the critical formula. */
export function rollDamage(formula: string, options: { critical?: boolean; physical?: boolean } = {}, supply: DiceSupply): DamageResult {
  const rolled = rollFormula(formula, supply);
  const instances = rolled.instances.map(instanceResult);
  const now = instances.filter((i) => !i.persistent);
  let total = now.filter((i) => i.kind !== "healing").reduce((s, i) => s + i.total, 0);
  const healing = now.filter((i) => i.kind !== "damage").reduce((s, i) => s + i.total, 0);
  let increasedFrom: number | null = null;
  if (now.some((i) => i.kind !== "healing") && total <= 0) {
    increasedFrom = total;
    total = 1;
  }
  return {
    formula,
    critical: options.critical ?? false,
    instances,
    total,
    healing,
    persistent: instances.filter((i) => i.persistent),
    splash: now.reduce((s, i) => s + i.splash, 0),
    precision: now.reduce((s, i) => s + i.precision, 0),
    increasedFrom,
    dice: rolled.dice,
    physical: options.physical ?? false,
  };
}

/** The dice a damage formula needs, for the physical entry. */
export function damageDice(formula: string) {
  return diceOf(formula);
}

/** "12 slashing + 4 fire, 3 persistent fire" */
export function describeDamage(result: DamageResult): string {
  const now = result.instances.filter((i) => !i.persistent).map((i) => `${i.total}${i.type === "untyped" ? "" : ` ${i.type}`}${i.kind === "healing" ? " healing" : i.kind === "both" ? " (damage or healing)" : ""}`);
  const later = result.persistent.map((i) => `${i.total} persistent ${i.type}`);
  const parts = [now.join(" + "), ...later].filter(Boolean);
  return `${parts.join(", ")}${result.critical ? " (critical)" : ""}`;
}
